///
/// EarthBrowser Admin Dashboard - Main Dashboard
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { AuditLogSection } from '@/components/admin/AuditLogSection'
import { EmailSection } from '@/components/admin/EmailSection'
import { FeatureFlagsSection } from '@/components/admin/FeatureFlagsSection'
import { GeoPackagesSection } from '@/components/admin/GeoPackagesSection'
import { InsightsSection } from '@/components/admin/InsightsSection'
import { OpsSection } from '@/components/admin/OpsSection'
import { RolesPermissionsSection } from '@/components/admin/RolesPermissionsSection'
import { SystemManagementSection } from '@/components/admin/SystemManagementSection'
import { UsersSection } from '@/components/admin/UsersSection'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import { useSignOutMutation } from '@/redux/api/userApi'
import { useAppSelector } from '@/redux/store'
import {
  Activity,
  BarChart3,
  Database,
  Flag,
  Gauge,
  Globe,
  LogOut,
  LucideIcon,
  Mail,
  Settings,
  Shield,
  Users,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface Section {
  id: string
  label: string
  title: string
  description: string
  icon: LucideIcon
  /// Semantic accent per design system: ops=rose, insights=indigo, users=blue, roles=purple,
  /// features=cyan, audit=emerald, email=orange, geopackages=teal, system=slate
  iconColor: string
  iconBg: string
  glow: string
  component: React.ComponentType
}

const sections: Section[] = [
  {
    id: 'ops',
    label: 'Ops',
    title: 'Operations',
    description: 'Service state at a glance and tasks that need your attention',
    icon: Gauge,
    iconColor: 'text-rose-600 dark:text-rose-400',
    iconBg: 'bg-rose-500/15',
    glow: 'from-rose-500 via-orange-500 to-amber-500',
    component: OpsSection,
  },
  {
    id: 'insights',
    label: 'Insights',
    title: 'Insights',
    description: 'Traffic, activation, engagement, authoring, retention and quality from first-party metrics',
    icon: BarChart3,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-500/15',
    glow: 'from-indigo-500 via-violet-500 to-purple-500',
    component: InsightsSection,
  },
  {
    id: 'users',
    label: 'Users',
    title: 'Users',
    description: 'Account management, roles, and trial-abuse analytics',
    icon: Users,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/15',
    glow: 'from-blue-600 via-cyan-500 to-teal-500',
    component: UsersSection,
  },
  {
    id: 'roles',
    label: 'Roles',
    title: 'Roles & Permissions',
    description: 'View and manage system roles and permissions',
    icon: Shield,
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/15',
    glow: 'from-purple-500 via-violet-500 to-purple-600',
    component: RolesPermissionsSection,
  },
  {
    id: 'features',
    label: 'Feature Flags',
    title: 'Feature Flags',
    description: 'Control feature availability and rollout across the application',
    icon: Flag,
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    iconBg: 'bg-cyan-500/15',
    glow: 'from-cyan-500 via-cyan-600 to-blue-600',
    component: FeatureFlagsSection,
  },
  {
    id: 'audit',
    label: 'Audit Log',
    title: 'Audit Logs',
    description: 'Track user actions and system events across the platform',
    icon: Activity,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-500/15',
    glow: 'from-emerald-500 via-teal-500 to-emerald-600',
    component: AuditLogSection,
  },
  {
    id: 'email',
    label: 'Email',
    title: 'Email',
    description: 'Template previews, send stats, and the suppression list',
    icon: Mail,
    iconColor: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-500/15',
    glow: 'from-amber-500 via-orange-500 to-orange-600',
    component: EmailSection,
  },
  {
    id: 'geopackages',
    label: 'GeoPackages',
    title: 'GeoPackage Catalog',
    description: 'Browse, monitor, and manage the GeoPackage data catalog',
    icon: Database,
    iconColor: 'text-teal-600 dark:text-teal-400',
    iconBg: 'bg-teal-500/15',
    glow: 'from-teal-500 via-cyan-500 to-teal-600',
    component: GeoPackagesSection,
  },
  {
    id: 'system',
    label: 'System',
    title: 'System Management',
    description: 'Manage system caches, services, and configurations',
    icon: Settings,
    iconColor: 'text-slate-600 dark:text-slate-400',
    iconBg: 'bg-slate-500/15',
    glow: 'from-slate-500 via-slate-400 to-slate-600',
    component: SystemManagementSection,
  },
]

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-lg blur-md opacity-75" />
        <div className="relative bg-blue-500/20 p-1.5 rounded-lg">
          <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
      <div className="flex flex-col">
        <span className="text-base font-bold leading-tight bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
          EarthBrowser
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground leading-tight">
          Admin Console
        </span>
      </div>
    </div>
  )
}

function NavButton({
  section,
  active,
  onSelect,
}: {
  section: Section
  active: boolean
  onSelect: (id: string) => void
}) {
  const Icon = section.icon
  return (
    <button
      onClick={() => onSelect(section.id)}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        active
          ? 'bg-accent text-accent-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? section.iconColor : ''}`} />
      <span className="whitespace-nowrap">{section.label}</span>
    </button>
  )
}

export function AdminDashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [signOut] = useSignOutMutation()

  const requested = searchParams.get('section')
  const activeSection = sections.find(s => s.id === requested) ?? sections[0]
  const ActiveComponent = activeSection.component
  const ActiveIcon = activeSection.icon

  const handleSelectSection = (id: string) => {
    setSearchParams(id === sections[0].id ? {} : { section: id })
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-border">
          <Brand />
        </div>

        <nav aria-label="Admin sections" className="flex-1 overflow-y-auto p-3 space-y-1">
          {sections.map(section => (
            <NavButton
              key={section.id}
              section={section}
              active={section.id === activeSection.id}
              onSelect={handleSelectSection}
            />
          ))}
        </nav>

        <div className="p-3 border-t border-border space-y-3">
          {userProfile && (
            <div className="px-2">
              <p className="text-sm font-medium truncate">
                {userProfile.first_name} {userProfile.last_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">{userProfile.email}</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              className="flex-1 rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
          <p className="px-2 text-[10px] text-muted-foreground">
            © {new Date().getFullYear()} Geodata Labs
          </p>
        </div>
      </aside>

      {/* Header + nav (mobile / tablet) */}
      <header className="lg:hidden sticky top-0 z-20 border-b border-border bg-card">
        <div className="px-4 py-3 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              aria-label="Sign out"
              className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <nav aria-label="Admin sections" className="flex gap-1 px-3 pb-2 overflow-x-auto">
          {sections.map(section => (
            <NavButton
              key={section.id}
              section={section}
              active={section.id === activeSection.id}
              onSelect={handleSelectSection}
            />
          ))}
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="relative shrink-0">
              <div className={`absolute inset-0 bg-gradient-to-br ${activeSection.glow} rounded-lg blur-md opacity-75`} />
              <div className={`relative ${activeSection.iconBg} p-2 rounded-lg`}>
                <ActiveIcon className={`h-5 w-5 ${activeSection.iconColor}`} />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground leading-tight">
                {activeSection.title}
              </h1>
              <p className="text-sm text-muted-foreground">{activeSection.description}</p>
            </div>
          </div>

          <ActiveComponent />
        </div>
      </main>
    </div>
  )
}
